import { z } from "zod/v4";

// Consumed fields from tripbears-api's tripListItemSchema. Retain all other
// fields, including optional expansions, for the raw response inspector.
const timestamp = z.union([z.iso.datetime(), z.iso.datetime({ precision: -1 })]);
const httpUrl = z.url({ protocol: /^https?$/ });
const rendition = z.looseObject({ url: httpUrl, width: z.number().int().positive(), height: z.number().int().positive(), expiresAt: timestamp.nullable() });
const image = z.looseObject({ id: z.string(), thumbnail: rendition, full: rendition, altText: z.string().nullable(), attribution: z.null() });
// The API resolves custom posters only. Country-image selection belongs to clients.
const cover = z.looseObject({ source: z.literal("CUSTOM"), countryCode: z.null(), images: z.tuple([image]) });
export const memberRoleSchema = z.enum(["OWNER", "MANAGER", "CONTRIBUTOR", "VIEWER", "PARTICIPANT"]);
export type MemberRole = z.infer<typeof memberRoleSchema>;
const tripMemberRefSchema = z.looseObject({
  id: z.string(), tripId: z.string(), name: z.string(), nickname: z.string().nullable(), role: memberRoleSchema,
});
export type TripMemberRef = z.infer<typeof tripMemberRefSchema>;
export const tripListItemSchema = z.looseObject({
  id: z.uuid(), name: z.string(),
  email: z.string().nullable(),
  startDate: timestamp.nullable(), endDate: timestamp.nullable(),
  isLocked: z.boolean(), isPublic: z.boolean(), cover: cover.nullable(),
  countries: z.array(z.string().regex(/^[A-Z]{2}$/)),
  countryVisits: z.array(z.looseObject({ countryCode: z.string().regex(/^[A-Z]{2}$/), startDate: z.iso.date().nullable(), endDate: z.iso.date().nullable() })),
  _count: z.looseObject({ events: z.number().int().nonnegative(), members: z.number().int().nonnegative() }),
  // Omission means the optional expansion was not returned, never an empty roster.
  members: z.array(tripMemberRefSchema).optional(),
  access: z.looseObject({ role: z.looseObject({ id: z.enum(["OWNER", "MANAGER", "CONTRIBUTOR", "VIEWER"]), name: z.enum(["Owner", "Manager", "Contributor", "Viewer"]) }).nullable(), permissions: z.record(z.string(), z.array(z.string())) }),
});
export const tripListSchema = z.array(tripListItemSchema);
export type TripListItem = z.infer<typeof tripListItemSchema>;
export const tripUsageSchema = z.looseObject({ trips: z.looseObject({ used: z.number().int().nonnegative(), limit: z.number().int().nonnegative().nullable(), remaining: z.number().int().nonnegative().nullable() }) });
export const tripBuckets = ["current", "upcoming", "past"] as const;
export type TripBucket = typeof tripBuckets[number];
export const tripListSearchSchema = z.object({ tab: z.enum(tripBuckets).catch("current").default("current"), q: z.string().max(200).catch("").default("") });
